<template>
	<div class="floating-cart-panel" v-show="has_items">
		<div class="floating-cart-header">
			<h6 class="floating-cart-title">{{ __("Your cart") }}</h6>
		</div>

		<div class="floating-cart-items">
			<div class="floating-cart-item" v-for="item in visible_items" :key="item.name">
				<div class="floating-cart-item-row">
					<span class="floating-cart-item-name">{{ item.product_name || item.product_id }}</span>
					<span v-if="item.promotion_badge" class="eshop-promo-badge" :style="item.promotion_badge.style"
						:lang="item.promotion_badge.title_lang || undefined">{{ item.promotion_badge.title }}</span>
					<span v-if="item.qty_in_basket > 1" class="floating-cart-item-qty">× {{ item.qty_in_basket }}</span>
				</div>
				<span v-if="item.product_id === BOOST_PRODUCT_ID && !item.unpriced" class="floating-cart-item-hint">{{ boost_price_hint(item) }}</span>
			</div>
			<div v-if="hidden_item_count > 0" class="floating-cart-more">
				{{ __("+{0} more", [hidden_item_count]) }}
			</div>
		</div>

		<!-- Never shown next to the notice: totals over a partly priced cart would be wrong. -->
		<div class="floating-cart-totals" v-if="!notice && (cart.subscription_total_in_basket > 0 || cart.setup_total_in_basket > 0)">
			<div class="floating-cart-total" v-if="cart.subscription_total_in_basket > 0">
				<span>{{ __("Monthly subscription") }}</span>
				<span>{{ ioi_format_currency(cart.subscription_total_in_basket || 0.0, cart.currency) }}</span>
			</div>
			<div class="floating-cart-total" v-if="cart.setup_total_in_basket > 0">
				<span>{{ __("One-off purchase") }}</span>
				<span>{{ ioi_format_currency(cart.setup_total_in_basket || 0.0, cart.currency) }}</span>
			</div>
		</div>

		<p v-if="notice" class="floating-cart-vat-note">{{ notice }}</p>

		<a class="btn btn-primary floating-cart-cta" href="/subscription">
			{{ __("See selection") }}
		</a>

		<p class="floating-cart-vat-note">{{ __("Prices exclude VAT") }}</p>
		<!-- A basket with a variable price: its note opens the rules of those of its products
		     that have one (PriceRuleTrigger.vue), and is plain text when none has. -->
		<p v-if="variable_lines.length" class="floating-cart-vat-note">
			<price-rule-trigger :rules="variable_rules" :text="__('contains variable price, see rules')" />
		</p>
	</div>
</template>

<script setup>
import { ioi_format_currency } from "../cart/format_currency";
import { BOOST_PRODUCT_ID } from "../cart/constants";
import PriceRuleTrigger from "../cart/PriceRuleTrigger.vue";
import { ref, computed, onMounted } from 'vue'

const VISIBLE_ITEM_CAP = 4

const cart = ref({})
// Only ever set for a guest whose cart could not be fully priced (apply_guest_cart).
const notice = ref("")

const has_items = computed(() => Object.keys(cart.value).length && cart.value.subscription_items.length)

// Most-recent-first: subscription_items comes back in child-table insertion
// order (oldest first), so slice(-N) grabs the N most recent, then reverse()
// puts the just-added item at the top, where the eye goes when the panel
// has just changed.
const visible_items = computed(() => (cart.value.subscription_items || []).slice(-VISIBLE_ITEM_CAP).reverse())
const hidden_item_count = computed(() => Math.max(0, (cart.value.subscription_items || []).length - VISIBLE_ITEM_CAP))

// Every basket line with a variable price, not only the ones listed: a logged-in line's
// stored variable_price, a guest line's from the catalogue (guest_cart.js to_line). An
// unpriced line has none.
const variable_lines = computed(() => (cart.value.subscription_items || []).filter(item => item.variable_price))
// The bubble lists those whose product has a rule, by name: each product once,
// whatever its lines.
const variable_rules = computed(() => variable_lines.value
	.filter(item => item.price_rule)
	.filter((item, index, items) => items.findIndex(other => other.product_id === item.product_id) === index)
	.map(item => ({ name: item.product_name || item.product_id, html: item.price_rule, lang: item.price_rule_lang })))

function boost_price_hint(item) {
	return __("+ {0} / user", [ioi_format_currency(item.enduser_price || 0.0, item.currency)])
}

onMounted(() => {
	get_subscription()
	ioi_partners.floating_shopping_cart_update.on("update_subscription", () => {
		get_subscription()
	})
})

function get_subscription() {
	// A guest has no server cart (the call's 403 would raise frappe's blocking modal):
	// the panel is built from the local guest cart instead.
	if (ioi_partners.is_guest()) {
		load_guest_cart()
		return
	}
	// Reseller mode without a focus has no cart to show (public/js/cart/focus.js): the panel
	// stays empty, so hidden, whatever the reseller's own company's basket holds.
	if (ioi_partners.is_focus_required()) {
		cart.value = {}
		return
	}
	frappe.call({
		method: "ioi_partners.www.subscription.get_subscription"
	}).then(r => {
		if (Object.keys(r.message).length) {
			const items = r.message.subscription_items
			cart.value = Object.assign({}, r.message, {subscription_items: items.filter(f => f.qty_in_basket > 0)});
		}
	})
}

// Guest counterpart of get_subscription(): quantities from the local guest cart,
// prices from the catalogue at render time (guest_cart.to_subscription). A newer
// change can land while a lookup is still in flight, so only the latest answer is
// applied.
let guest_request = 0

function load_guest_cart() {
	const request = ++guest_request
	ioi_partners.guest_cart.to_subscription()
		.then(subscription => {
			if (request === guest_request) {
				apply_guest_cart(subscription)
			}
		})
		.catch(error => {
			// Not expected - to_subscription() handles its own lookup failures - so it is
			// logged, and shown like a failed lookup rather than leaving the panel frozen.
			console.error("Guest cart could not be built", error)
			if (request === guest_request) {
				apply_guest_cart(null)
			}
		})
}

// A failed price lookup must never look like an empty cart. The panel keeps the lines
// that could be priced, lists the rest by product code with no price, and drops the
// totals - they would cover only part of the cart - behind a notice to refresh. This
// is preferred over keeping the previous state, which is empty on a first load and
// would also hide a product just added.
function apply_guest_cart(subscription) {
	const stored = ioi_partners.guest_cart.items()

	if (subscription && !subscription.failed) {
		notice.value = ""
		cart.value = subscription.subscription_items.length ? subscription : {}
		return
	}

	if (!stored.length) {
		notice.value = ""
		cart.value = {}
		return
	}

	const priced = subscription ? subscription.subscription_items : []
	const unpriced = subscription ? subscription.unpriced : stored
	notice.value = __("Some prices could not be loaded. Refresh the page to try again.")
	cart.value = {
		subscription_items: priced.concat(unpriced.map(item => ({
			name: item.product_id,
			product_id: item.product_id,
			qty_in_basket: item.qty,
			unpriced: true,
		}))),
	}
}

</script>